import { useSignIn, useSignUp } from "@clerk/clerk-react"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { analyticsEvents } from "@/lib/analytics/events"
import { trackEvent } from "@/lib/analytics/posthog"
import {
  normalizeFirstName,
  normalizeLastInitial,
} from "@/lib/playerName"

type AuthStage = "identifier" | "code" | "requirements"
type AuthFlow = "sign_in" | "sign_up"
type AuthMethod = "email" | "phone"

type EmailCodeFactor = {
  strategy: "email_code"
  emailAddressId: string
}

type PhoneCodeFactor = {
  strategy: "phone_code"
  phoneNumberId: string
}

function isEmailCodeFactor(factor: unknown): factor is EmailCodeFactor {
  return (
    typeof factor === "object" &&
    factor !== null &&
    (factor as { strategy?: unknown }).strategy === "email_code" &&
    typeof (factor as { emailAddressId?: unknown }).emailAddressId === "string"
  )
}

function isPhoneCodeFactor(factor: unknown): factor is PhoneCodeFactor {
  return (
    typeof factor === "object" &&
    factor !== null &&
    (factor as { strategy?: unknown }).strategy === "phone_code" &&
    typeof (factor as { phoneNumberId?: unknown }).phoneNumberId === "string"
  )
}

function getEmailCodeFactor(factors: unknown[] | null | undefined) {
  return factors?.find(isEmailCodeFactor) ?? null
}

function getPhoneCodeFactor(factors: unknown[] | null | undefined) {
  return factors?.find(isPhoneCodeFactor) ?? null
}

export const Route = createFileRoute("/sign-in")({
  head: () => ({
    meta: [
      { title: "Private Access" },
      { name: "description", content: "Private access." },
    ],
  }),
  validateSearch: (search) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: PrivateSignInPage,
})

function normalizeEmail(raw: string) {
  return raw.trim().toLowerCase()
}

function normalizePhone(raw: string) {
  const digits = phoneInputDigits(raw)
  return digits.length === 10 ? `+1${digits}` : raw.trim()
}

function phoneInputDigits(raw: string) {
  const digits = raw.replace(/\D/g, "")
  const national = digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits
  return national.slice(0, 10)
}

function normalizeIdentifier(method: AuthMethod, raw: string) {
  return method === "email" ? normalizeEmail(raw) : normalizePhone(raw)
}

function looksLikeEmail(raw: string) {
  const email = normalizeEmail(raw)
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function looksLikePhone(raw: string) {
  return phoneInputDigits(raw).length === 10
}

function clerkMessage(error: unknown, fallback: string) {
  const maybeError = error as {
    errors?: { code?: string; longMessage?: string; message?: string }[]
    message?: string
  }
  return (
    maybeError.errors?.[0]?.longMessage ??
    maybeError.errors?.[0]?.message ??
    maybeError.message ??
    fallback
  )
}

function clerkCode(error: unknown) {
  return (error as { errors?: { code?: string }[] }).errors?.[0]?.code
}

function isAllowedConsoleRedirect(path: string | undefined) {
  return (
    path === "/host" ||
    path?.startsWith("/host/") ||
    path === "/admin" ||
    path?.startsWith("/admin/")
  )
}

function safeRedirectTarget(searchRedirect?: string): string {
  if (isAllowedConsoleRedirect(searchRedirect)) return searchRedirect ?? "/host"
  if (typeof window === "undefined") return "/host"
  const pending = window.localStorage.getItem("wgt_post_signin")
  if (isAllowedConsoleRedirect(pending ?? undefined)) {
    window.localStorage.removeItem("wgt_post_signin")
    return pending ?? "/host"
  }
  return "/host"
}

function codeSource(method: AuthMethod) {
  return method === "email" ? "email" : "text"
}

function contactLabel(method: AuthMethod) {
  return method === "email" ? "Email address" : "10-digit phone number"
}

function inputPlaceholder(method: AuthMethod) {
  return method === "email" ? "you@example.com" : "5551234567"
}

function displayContact(method: AuthMethod, contact: string) {
  return method === "email" ? contact : phoneInputDigits(contact)
}

function codeButtonLabel(method: AuthMethod, busy: boolean) {
  if (busy) return "Sending..."
  return method === "email" ? "Email me a code" : "Text me a code"
}

function trackCodeRequested(method: AuthMethod, properties: Record<string, string>) {
  trackEvent(
    method === "email"
      ? analyticsEvents.emailCodeRequested
      : analyticsEvents.phoneCodeRequested,
    properties,
  )
}

function trackCodeRequestFailed(
  method: AuthMethod,
  properties: Record<string, string>,
) {
  trackEvent(
    method === "email"
      ? analyticsEvents.emailCodeRequestFailed
      : analyticsEvents.phoneCodeRequestFailed,
    properties,
  )
}

function trackAuthCompleted(
  method: AuthMethod,
  properties: Record<string, string>,
) {
  trackEvent(
    method === "email"
      ? analyticsEvents.emailAuthCompleted
      : analyticsEvents.phoneAuthCompleted,
    properties,
  )
}

function trackAuthFailed(
  method: AuthMethod,
  properties: Record<string, string>,
) {
  trackEvent(
    method === "email"
      ? analyticsEvents.emailAuthFailed
      : analyticsEvents.phoneAuthFailed,
    properties,
  )
}

function PrivateSignInPage() {
  const { redirect } = Route.useSearch()
  const {
    isLoaded: signInLoaded,
    signIn,
    setActive: setSignInActive,
  } = useSignIn()
  const {
    isLoaded: signUpLoaded,
    signUp,
    setActive: setSignUpActive,
  } = useSignUp()
  const [stage, setStage] = useState<AuthStage>("identifier")
  const [flow, setFlow] = useState<AuthFlow>("sign_in")
  const [method, setMethod] = useState<AuthMethod>("email")
  const [identifier, setIdentifier] = useState("")
  const [pendingSignUpContact, setPendingSignUpContact] = useState<string | null>(null)
  const [code, setCode] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastInitial, setLastInitial] = useState("")
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const loaded = signInLoaded && signUpLoaded
  const profileFirstName = normalizeFirstName(firstName)
  const profileLastInitial = normalizeLastInitial(lastInitial)
  const profileComplete = !!profileFirstName && !!profileLastInitial

  async function activate(sessionId: string | null, source: AuthFlow) {
    if (!sessionId) throw new Error("Clerk did not return a session.")
    const setActive = source === "sign_in" ? setSignInActive : setSignUpActive
    if (!setActive) throw new Error("Clerk is still loading.")
    await setActive({ session: sessionId })
    trackAuthCompleted(method, { flow: source })
    window.location.href = safeRedirectTarget(redirect)
  }

  async function prepareSignInCode(contact: string) {
    if (!signIn) return
    const attempt = await signIn.create({ identifier: contact })

    if (method === "email") {
      const emailCodeFactor = getEmailCodeFactor(attempt.supportedFirstFactors)
      if (!emailCodeFactor) {
        throw new Error(
          "Email-code sign-in is not enabled for this Clerk application.",
        )
      }
      await signIn.prepareFirstFactor({
        strategy: "email_code",
        emailAddressId: emailCodeFactor.emailAddressId,
      })
      return attempt.identifier ?? contact
    }

    const phoneCodeFactor = getPhoneCodeFactor(attempt.supportedFirstFactors)
    if (!phoneCodeFactor) {
      throw new Error(
        "Phone-code sign-in is not enabled for this Clerk application.",
      )
    }
    await signIn.prepareFirstFactor({
      strategy: "phone_code",
      phoneNumberId: phoneCodeFactor.phoneNumberId,
    })
    return attempt.identifier ?? contact
  }

  async function prepareSignUpCode(contact: string) {
    if (!signUp) return

    if (method === "email") {
      await signUp.create({
        emailAddress: contact,
        firstName: profileFirstName,
        lastName: profileLastInitial,
        legalAccepted: true,
      })
      await signUp.prepareEmailAddressVerification({
        strategy: "email_code",
      })
      return
    }

    await signUp.create({
      phoneNumber: contact,
      firstName: profileFirstName,
      lastName: profileLastInitial,
      legalAccepted: true,
    })
    await signUp.preparePhoneNumberVerification({
      strategy: "phone_code",
    })
  }

  async function sendCode() {
    if (!loaded || !signIn || !signUp) return
    const contact = normalizeIdentifier(method, identifier)
    const valid =
      method === "email" ? looksLikeEmail(contact) : looksLikePhone(contact)
    if (!valid) {
      setMessage(
        method === "email"
          ? "Use a valid email address."
          : "Enter a 10-digit phone number.",
      )
      return
    }

    setBusy(true)
    setMessage("Sending code...")
    try {
      const sentTo = await prepareSignInCode(contact)
      setFlow("sign_in")
      setStage("code")
      setMessage(`Code sent to ${displayContact(method, sentTo ?? contact)}.`)
      trackCodeRequested(method, {
        flow: "sign_in",
        status: "sent",
      })
    } catch (error) {
      const code = clerkCode(error)
      if (
        code === "form_identifier_not_found" ||
        code === "identifier_not_found"
      ) {
        setPendingSignUpContact(contact)
        setFlow("sign_up")
        setStage("requirements")
        setMessage("Add your player name to get a code.")
      } else {
        trackCodeRequestFailed(method, {
          flow: "sign_in",
          errorCode: code ?? "unknown",
        })
        setMessage(clerkMessage(error, "Couldn't send a code."))
      }
    } finally {
      setBusy(false)
    }
  }

  async function verifyCode(codeOverride?: string) {
    if (!loaded || !signIn || !signUp) return
    const codeToVerify = (codeOverride ?? code).trim()
    if (codeToVerify.length < 4) {
      setMessage(`Enter the code from your ${codeSource(method)}.`)
      return
    }

    setBusy(true)
    setMessage("Checking code...")
    try {
      if (flow === "sign_in") {
        const result =
          method === "email"
            ? await signIn.attemptFirstFactor({
                strategy: "email_code",
                code: codeToVerify,
              })
            : await signIn.attemptFirstFactor({
                strategy: "phone_code",
                code: codeToVerify,
              })
        if (result.status === "complete") {
          await activate(result.createdSessionId, "sign_in")
          return
        }
        setMessage(`Sign-in needs another step: ${result.status}.`)
        trackAuthFailed(method, {
          flow,
          status: result.status ?? "unknown",
        })
        return
      }

      const result =
        method === "email"
          ? await signUp.attemptEmailAddressVerification({
              code: codeToVerify,
            })
          : await signUp.attemptPhoneNumberVerification({
              code: codeToVerify,
            })
      if (result.status === "complete") {
        trackEvent(analyticsEvents.playerProfileCompleted, {
          source: "console_sign_up",
        })
        await activate(result.createdSessionId, "sign_up")
        return
      }
      if (result.status === "missing_requirements") {
        setStage("requirements")
        setMessage("Add your player name to finish the account.")
        return
      }
      setMessage(`Sign-up needs another step: ${result.status}.`)
      trackAuthFailed(method, {
        flow,
        status: result.status ?? "unknown",
      })
    } catch (error) {
      trackAuthFailed(method, {
        flow,
        errorCode: clerkCode(error) ?? "unknown",
      })
      setMessage(clerkMessage(error, "That code did not work."))
    } finally {
      setBusy(false)
    }
  }

  async function completeRequirements() {
    if (!loaded || !signUp) return
    if (!profileComplete) {
      setMessage("Enter your first name and last initial.")
      return
    }

    setBusy(true)
    setMessage(pendingSignUpContact ? "Sending code..." : "Finishing account...")
    try {
      if (pendingSignUpContact) {
        await prepareSignUpCode(pendingSignUpContact)
        setStage("code")
        setMessage(`Code sent to ${displayContact(method, pendingSignUpContact)}.`)
        setPendingSignUpContact(null)
        trackCodeRequested(method, {
          flow: "sign_up",
          status: "sent",
        })
        return
      }

      const result = await signUp.update({
        firstName: profileFirstName,
        lastName: profileLastInitial,
        legalAccepted: true,
      })
      if (result.status === "complete") {
        trackEvent(analyticsEvents.playerProfileCompleted, {
          source: "console_sign_up_requirements",
        })
        await activate(result.createdSessionId, "sign_up")
        return
      }
      setMessage(`Sign-up needs another step: ${result.status}.`)
      trackAuthFailed(method, {
        flow: "sign_up",
        status: result.status ?? "unknown",
      })
    } catch (error) {
      trackAuthFailed(method, {
        flow: "sign_up",
        errorCode: clerkCode(error) ?? "unknown",
      })
      setMessage(clerkMessage(error, "Couldn't finish your account."))
    } finally {
      setBusy(false)
    }
  }

  function handleCodeChange(value: string) {
    const digits = value.replace(/\D/g, "").slice(0, 6)
    setCode(digits)
    if (digits.length === 6) void verifyCode(digits)
  }

  function resetIdentifierStage() {
    setStage("identifier")
    setPendingSignUpContact(null)
    setCode("")
    setMessage(null)
  }

  function switchMethod(nextMethod: AuthMethod) {
    if (busy || stage !== "identifier") return
    setMethod(nextMethod)
    setIdentifier("")
    setPendingSignUpContact(null)
    setCode("")
    setMessage(null)
  }

  function handleIdentifierChange(value: string) {
    setIdentifier(method === "phone" ? phoneInputDigits(value) : value)
  }

  return (
    <main className="relative flex min-h-screen flex-col overflow-hidden bg-[color:var(--background)] px-6 text-[color:var(--foreground)]">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 18%, color-mix(in oklab, var(--primary) 24%, transparent) 0%, transparent 55%), radial-gradient(circle at 86% 78%, color-mix(in oklab, var(--accent) 18%, transparent) 0%, transparent 48%)",
        }}
      />
      <section className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-20">
        <div className="rounded-[28px] border border-[color:var(--border)] bg-[color:var(--background)]/80 p-6 shadow-[0_24px_70px_-36px_rgba(0,0,0,0.7)] backdrop-blur-md">
          <div className="mb-6 flex flex-col items-center text-center">
            <div
              aria-hidden="true"
              className="mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-[color:var(--border)] bg-[color:var(--muted)]"
            >
              <span className="h-2.5 w-2.5 rounded-full bg-[color:var(--accent)] shadow-[0_0_18px_var(--accent)]" />
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--accent)]">
              Private access
            </p>
            <h1 className="mt-2 font-display text-3xl leading-none">
              Sign in
            </h1>
            <p className="mt-3 text-sm leading-6 text-[color:var(--muted-foreground)]">
              Use email or phone to continue.
            </p>
          </div>

          {stage === "identifier" ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 rounded-full border border-[color:var(--border)] bg-[color:var(--muted)] p-1">
                {(["email", "phone"] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => switchMethod(option)}
                    className={[
                      "h-10 rounded-full font-display text-sm transition",
                      method === option
                        ? "bg-[color:var(--primary)] text-[color:var(--primary-foreground)] shadow-[0_12px_30px_-18px_rgba(71,71,221,0.9)]"
                        : "text-[color:var(--muted-foreground)] hover:text-[color:var(--foreground)]",
                    ].join(" ")}
                  >
                    {option === "email" ? "Email" : "Phone"}
                  </button>
                ))}
              </div>
              <label className="block">
                <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted-foreground)]">
                  {contactLabel(method)}
                </span>
                <input
                  value={identifier}
                  onChange={(event) => handleIdentifierChange(event.target.value)}
                  type={method === "email" ? "email" : "tel"}
                  inputMode={method === "email" ? "email" : "numeric"}
                  autoComplete={method === "email" ? "email" : "tel-national"}
                  autoCapitalize="none"
                  autoCorrect="off"
                  placeholder={inputPlaceholder(method)}
                  maxLength={method === "phone" ? 10 : undefined}
                  className="h-12 w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--muted)] px-4 text-base outline-none transition focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)]/25"
                />
              </label>
              <button
                onClick={sendCode}
                disabled={busy || !loaded}
                className="h-12 w-full rounded-full bg-[color:var(--primary)] px-5 font-display text-base text-[color:var(--primary-foreground)] shadow-[0_14px_36px_-18px_rgba(71,71,221,0.8)] transition hover:translate-y-[-1px] disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {codeButtonLabel(method, busy)}
              </button>
            </div>
          ) : null}

          {stage === "code" ? (
            <div className="space-y-3">
              <label className="block">
                <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted-foreground)]">
                  Verification code
                </span>
                <input
                  value={code}
                  onChange={(event) => handleCodeChange(event.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  maxLength={6}
                  className="h-14 w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--muted)] px-4 text-center font-mono text-2xl font-black tabular-nums outline-none transition focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)]/25"
                />
              </label>
              <button
                onClick={() => verifyCode()}
                disabled={busy || code.length < 4}
                className="h-12 w-full rounded-full bg-[color:var(--primary)] px-5 font-display text-base text-[color:var(--primary-foreground)] shadow-[0_14px_36px_-18px_rgba(71,71,221,0.8)] transition hover:translate-y-[-1px] disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? "Checking..." : "Verify code"}
              </button>
              <button
                onClick={resetIdentifierStage}
                className="h-9 w-full font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted-foreground)] transition hover:text-[color:var(--foreground)]"
              >
                Use a different {method === "email" ? "email" : "number"}
              </button>
            </div>
          ) : null}

          {stage === "requirements" ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  placeholder="First"
                  autoComplete="given-name"
                  className="h-12 rounded-xl border border-[color:var(--border)] bg-[color:var(--muted)] px-4 text-base outline-none transition focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)]/25"
                />
                <input
                  value={lastInitial}
                  onChange={(event) =>
                    setLastInitial(normalizeLastInitial(event.target.value))
                  }
                  placeholder="Last initial"
                  autoCapitalize="characters"
                  maxLength={1}
                  className="h-12 rounded-xl border border-[color:var(--border)] bg-[color:var(--muted)] px-4 text-base outline-none transition focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)]/25"
                />
              </div>
              <button
                onClick={completeRequirements}
                disabled={busy || !profileComplete}
                className="h-12 w-full rounded-full bg-[color:var(--primary)] px-5 font-display text-base text-[color:var(--primary-foreground)] shadow-[0_14px_36px_-18px_rgba(71,71,221,0.8)] transition hover:translate-y-[-1px] disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy
                  ? pendingSignUpContact
                    ? "Sending..."
                    : "Finishing..."
                  : pendingSignUpContact
                    ? codeButtonLabel(method, false)
                    : "Finish account"}
              </button>
              {pendingSignUpContact ? (
                <button
                  onClick={resetIdentifierStage}
                  className="h-9 w-full font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted-foreground)] transition hover:text-[color:var(--foreground)]"
                >
                  Use a different {method === "email" ? "email" : "number"}
                </button>
              ) : null}
            </div>
          ) : null}

          {message ? (
            <p className="mt-4 text-center text-sm leading-5 text-[color:var(--muted-foreground)]">
              {message}
            </p>
          ) : null}
        </div>
      </section>
    </main>
  )
}
